import { TextButton } from "@/components/ui/buttons";
import { useOpenWeeklyBalanceCoach } from "@/features/overview/hooks/use-open-weekly-balance-coach";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo } from "react";
import { ProgressBarTrack, ProgressBreakpoints, SegmentMaxPercentages } from "./progress-bar-track";

const RANGE_LABELS = ["Smaller", "Target Deficit", "Larger"] as const;

interface CalorieProgressBarProps {
  value: number;
  breakpoints: ProgressBreakpoints;
  isLoading?: boolean;
  segmentMaxPercentages?: SegmentMaxPercentages;
}

export const CalorieProgressBar = memo(function CalorieProgressBar({
  value,
  breakpoints,
  isLoading = false,
  segmentMaxPercentages,
}: CalorieProgressBarProps) {
  const openWeeklyBalanceCoach = useOpenWeeklyBalanceCoach();
  if (isLoading) {
    return (
      <div className="mt-2 w-full text-left">
        <IonSkeletonText animated className="h-21 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mt-2 w-full text-left">
      <div className="grid grid-cols-3">
        {RANGE_LABELS.map((label, index) => {
          return (
            <p
              key={label}
              className={cn(textVariants.footnote, "text-white", {
                "text-center": index !== 0 && index !== RANGE_LABELS.length - 1,
                "text-left": index === 0,
                "text-right": index === RANGE_LABELS.length - 1,
              })}
            >
              {label}
            </p>
          );
        })}
      </div>

      <ProgressBarTrack
        breakpoints={breakpoints}
        value={value}
        segmentMaxPercentages={segmentMaxPercentages}
      />

      <TextButton
        className={cn(
          textVariants.footnote,
          "mx-auto mt-2 flex h-6 w-auto items-center justify-center gap-1 !py-0",
        )}
        aria-label="How your weekly calorie deficit works"
        onClick={openWeeklyBalanceCoach}
      >
        <span>How your weekly calorie deficit works</span>
        <img
          src="/assets/icons/weekly-deficit-arrow-right.svg"
          alt=""
          aria-hidden
          className="size-4 shrink-0"
        />
      </TextButton>
    </div>
  );
});
